import { createClient, SupabaseClient } from '@supabase/supabase-js';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL!;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY!;

/**
 * Public campaign service for accessing campaign data
 * 
 * Supports two modes:
 * 1. Service token (cookie __service_token): Uses service_role key to bypass RLS
 *    - Used by Playwright screenshot worker on VPS
 * 2. Fallback (anon key): Standard read access (RLS enforced - user sees own campaigns only)
 */

function getServiceTokenFromCookie(): string | null {
  try {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, ...rest] = cookie.trim().split('=');
      if (name === '__service_token' && rest.length > 0) {
        return rest.join('=');
      }
    }
  } catch {
    // Cookie access may fail in some environments
  }
  return null;
}

function createServiceClient(): SupabaseClient | null {
  const token = getServiceTokenFromCookie();
  if (!token) return null;

  try {
    const parts = token.split('.');
    if (parts.length !== 3) {
      console.warn('⚠️ Invalid service token format');
      return null;
    }

      return createClient(supabaseUrl, token, {
      auth: {
        autoRefreshToken: false,
        persistSession: false,
        detectSessionInUrl: false,
      },
      db: { schema: 'public' },
    });
  } catch {
    console.warn('⚠️ Failed to create service client, falling back to anon');
    return null;
  }
}

function getClient(): SupabaseClient {
  return createServiceClient() || createClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      autoRefreshToken: true,
      persistSession: true,
      detectSessionInUrl: false,
    },
    db: { schema: 'public' },
  });
}

async function getCampaignByIdPublic(campaignId: string): Promise<{ success: boolean; data?: any; error?: string }> {
  try {
    const client = getClient();
    const { data: campaign, error: campaignError } = await client
      .from('campaigns')
      .select(`
        *,
        ad_content!ad_content_campaign_id_fkey (*),
        campaign_nodes (
          id,
          node_id,
          campaign_id,
          created_at,
          nodes:node_id (
            id,
            name,
            location,
            price,
            device_type,
            status,
            description
          )
        ),
        qr_codes!fk_qr_codes_campaign (
          id,
          qr_code_url,
          destination_url,
          cta,
          cta_icon,
          qr_type,
          status,
          design_settings
        )
      `)
      .eq('id', campaignId)
      .single();

    if (campaignError) {
      console.error('❌ Error fetching campaign:', campaignError);
      return { success: false, error: campaignError.message };
    }

    if (!campaign) {
      return { success: false, error: 'Campaign not found' };
    }

    const campaignNodeData = campaign.campaign_nodes || [];
    const selected_nodes = campaignNodeData.map((cn: any) => cn.node_id);
    const nodeDetails = campaignNodeData.map((cn: any) => cn.nodes).filter(Boolean);

    const qrCodeData = campaign.qr_codes && Array.isArray(campaign.qr_codes) && campaign.qr_codes.length > 0
      ? campaign.qr_codes[0]
      : campaign.qr_codes;

    const enrichedCampaign = {
      ...campaign,
      selected_nodes,
      direct_node: nodeDetails.length > 0 ? nodeDetails[0] : null,
      selectedNodeDetails: nodeDetails,
      nodes: nodeDetails,
      campaign_nodes: campaignNodeData,
      ad_content: campaign.ad_content || null,
      qr_code: qrCodeData,
      qr_code_url: qrCodeData?.qr_code_url,
      destination_url: qrCodeData?.destination_url,
      qr_image_url: qrCodeData?.qr_code_url,
      qr_cta: qrCodeData?.cta,
      qr_type: qrCodeData?.qr_type || campaign.qr_type,
      qr_status: qrCodeData?.status,
      qr_design_settings: qrCodeData?.design_settings,
      qr_id: qrCodeData?.id,
      qr_url: qrCodeData?.qr_code_url
    };

    return { success: true, data: enrichedCampaign };
  } catch (error) {
    console.error('💥 Error in getCampaignByIdPublic:', error);
    return { success: false, error: 'Unexpected error occurred' };
  }
}

export const publicCampaignService = {
  getCampaignByIdPublic,
};